/**
 * navigation/platformAuthRedirect.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SK Hire does NOT own authentication. The SK Platform owns the only
 * login / signup / forgot-password screens for every product. When this app
 * has no valid session, we bounce the user to the centralized platform auth,
 * carrying the product handoff:
 *     ?product=hire&return_to=<where they were>&state=<nonce>
 * and a short-lived `sk_auth_state` cookie (parent-domain scoped) the platform
 * verifies (CSRF / first-party-initiation). After auth, the platform redirects
 * back here, logged in via the shared session cookie.
 *
 * The `sk_auth_state` cookie name and the platform-auth host resolution are
 * product-agnostic — they MUST match every other SK product so the platform
 * recognizes the handoff. Only PRODUCT differs.
 *
 * Web-only; no-op on native.
 */
import { Platform } from 'react-native';

function resolvePlatformAuthUrl(): string {
  if (typeof window === 'undefined') return 'https://platform.softknack.com/';
  const h = window.location.hostname;
  if (/^(localhost|127\.0\.0\.1)$/.test(h)) return 'http://localhost:3009/';
  if (h.includes('.dev.')) return 'https://platform.dev.softknack.com/';
  if (h.includes('.staging.')) return 'https://platform.staging.softknack.com/';
  return 'https://platform.softknack.com/';
}
const PLATFORM_AUTH_URL = resolvePlatformAuthUrl();
const PRODUCT = 'hire';
const STATE_COOKIE = 'sk_auth_state';

const isWeb = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

function randomState(): string {
  const bytes = new Uint8Array(16);
  (window.crypto as Crypto).getRandomValues(bytes);
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) hex += bytes[i].toString(16).padStart(2, '0');
  return hex;
}

function setStateCookie(state: string): void {
  const parts = window.location.hostname.split('.');
  const domain = parts.length >= 2 && parts[parts.length - 1] !== 'localhost'
    ? `; domain=.${parts.slice(-2).join('.')}`
    : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${STATE_COOKIE}=${state}; Max-Age=600; path=/; SameSite=Lax${domain}${secure}`;
}

let _redirecting = false;

/** Bounce to the centralized platform auth, returning here after sign-in. */
export function redirectToPlatformAuth(): void {
  if (!isWeb() || _redirecting) return;
  _redirecting = true;

  const state = randomState();
  setStateCookie(state);

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('product', PRODUCT);
  url.searchParams.set('return_to', window.location.href);
  url.searchParams.set('state', state);
  window.location.assign(url.toString());
}

/**
 * Safety net for the legacy email-link contract: if a verify/reset email was
 * ever addressed to this SPA (?mode=verify|reset&token=…), forward the token
 * straight to the platform auth app, which owns those flows. With the platform
 * registry pointing hire's auth-email base at the platform, emails should land
 * there directly and these forwarders never fire — but they keep old links safe.
 */
export function redirectToPlatformVerify(token: string): void {
  if (!isWeb() || _redirecting) return;
  _redirecting = true;

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('mode', 'verify');
  url.searchParams.set('token', token);
  window.location.assign(url.toString());
}

export function takeVerifyToken(): string | null {
  if (!isWeb()) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') !== 'verify') return null;
  const token = params.get('token');
  if (!token) return null;
  window.history.replaceState({}, '', window.location.pathname);
  return token;
}

export function redirectToPlatformReset(token: string): void {
  if (!isWeb() || _redirecting) return;
  _redirecting = true;

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('mode', 'reset');
  url.searchParams.set('token', token);
  window.location.assign(url.toString());
}

export function takeResetToken(): string | null {
  if (!isWeb()) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') !== 'reset') return null;
  const token = params.get('token');
  if (!token) return null;
  window.history.replaceState({}, '', window.location.pathname);
  return token;
}
